import React, { useEffect, useState, useRef } from 'react'

// Global cache to prevent duplicate get_active_theme requests
// Use a simple Map for immediate synchronous access
const activeThemePromises = new Map()
const CACHE_DURATION = 5000 // 5 seconds

const getCachedActiveTheme = (context, userBrowserThemeSetting, additionalParams = {}) => {
  // Don't cache manual override requests - they should always be fresh
  if (additionalParams.userManualOverride) {
    return (async () => {
      const siteThemeAPI = (await import('aldoo-ra/API')).api('site-theme')
      return siteThemeAPI({
        get_active_theme: {
          context,
          userBrowserThemeSetting,
          ...additionalParams
        }
      })
    })()
  }

  const cacheKey = `${context}_${userBrowserThemeSetting}_${JSON.stringify(additionalParams)}`

  // Check if we already have a pending request for this exact combination
  if (activeThemePromises.has(cacheKey)) {
    return activeThemePromises.get(cacheKey)
  }

  // Create the promise and immediately cache it (synchronously)
  const promise = (async () => {
    try {
      const siteThemeAPI = (await import('aldoo-ra/API')).api('site-theme')
      const result = await siteThemeAPI({
        get_active_theme: {
          context,
          userBrowserThemeSetting,
          ...additionalParams
        }
      })
      return result
    } catch (error) {
      // Handle 404 (no active theme) gracefully - this is expected behavior
      if (error?.response?.status === 404 || error?.message?.includes('No active theme found')) {
        return { error: 'No active theme found', isExpected: true }
      }

      // For other errors, still throw to maintain error handling
      throw error
    } finally {
      // Clean up after completion
      setTimeout(() => {
        if (activeThemePromises.get(cacheKey) === promise) {
          activeThemePromises.delete(cacheKey)
        }
      }, CACHE_DURATION)
    }
  })()

  // Cache the promise IMMEDIATELY (synchronously) before returning
  activeThemePromises.set(cacheKey, promise)

  return promise
}

/**
 * BrowserThemeDetector Hook
 *
 * Detects user's browser/system theme preference and provides auto-adaptation functionality
 * Integrates with the aldoo-ra SiteTheme system for automatic theme switching
 *
 * @returns {Object} Browser theme detection state and methods
 */
export const useBrowserThemeDetector = () => {
  const [browserTheme, setBrowserTheme] = useState('light')
  const [autoAdaptEnabled, setAutoAdaptEnabled] = useState(false)
  const [isSupported, setIsSupported] = useState(false)
  const mediaQueryRef = useRef(null)
  const lastAppliedTheme = useRef(null)

  // Initialize browser theme detection
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Check if browser supports prefers-color-scheme
    const supportsColorScheme = window.matchMedia && window.matchMedia('(prefers-color-scheme)').media === '(prefers-color-scheme)'
    setIsSupported(supportsColorScheme)

    if (!supportsColorScheme) {
      console.warn('BrowserThemeDetector: Browser does not support prefers-color-scheme media query')
      return
    }

    // Create media query for dark mode detection
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQueryRef.current = darkModeQuery

    // Set initial theme
    const initialTheme = darkModeQuery.matches ? 'dark' : 'light'
    setBrowserTheme(initialTheme)

    // Listen for changes
    const handleThemeChange = (e) => {
      const newTheme = e.matches ? 'dark' : 'light'
      setBrowserTheme(newTheme)
    }

    // Add listener
    darkModeQuery.addEventListener('change', handleThemeChange)

    // Cleanup
    return () => {
      if (darkModeQuery) {
        darkModeQuery.removeEventListener('change', handleThemeChange)
      }
    }
  }, [])

  // Auto-adapt theme when browser theme changes and auto-adaptation is enabled
  useEffect(() => {

    if (!autoAdaptEnabled || !isSupported || typeof window === 'undefined') {
      return
    }

    const applyAutoAdaptedTheme = async () => {
      try {
        // Check if auto-adaptation is disabled in ParamIO (site-wide setting)
        const { GetParam } = await import('../Admin/ParamIO/api')
        const autoDetectionParam = await GetParam('SITE_THEME_AUTO_ADAPT').catch(() => null)
        const siteWideAutoAdaptEnabled = autoDetectionParam?.value === true || autoDetectionParam?.value === 'true'

        if (!siteWideAutoAdaptEnabled) {
          return // Auto-adaptation disabled site-wide
        }

        // Check if user has manual theme preference - if so, don't auto-adapt
        const LocalUserPreferences = (await import('aldoo-ra/LocalUserPreferences')).default
        const currentContext = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin') ? 'admin' : 'site'
        const contextPreferenceKey = currentContext === 'admin' ? 'ui.theme.admin.selectedId' : 'ui.theme.site.selectedId'
        const userManualPreference = LocalUserPreferences.get(contextPreferenceKey)

        if (userManualPreference) {
          return // User has manually set a preference, don't override
        }

        // Prevent unnecessary API calls if theme hasn't changed
        if (lastAppliedTheme.current === browserTheme) {
          return
        }

        // Check if SiteTheme is available for manual theme changes
        if (window.SiteTheme) {
          // Get current theme to check if it should be auto-adapted
          const currentTheme = window.SiteTheme.getTheme()
          const currentContext = window.location.pathname.startsWith('/admin') ? 'admin' : 'site'

          // Call get_active_theme API with browser theme setting (cached)
          const response = await getCachedActiveTheme(currentContext, browserTheme)

          // Check if this is our expected "no theme found" response
          if (response.error && response.isExpected) {
            // This is expected behavior - no theme configured, skip gracefully
            return
          }

          if (response.result && response.result !== currentTheme?._id) {
            // Apply the auto-adapted theme
            await window.SiteTheme.setTheme(response.result, true)
            lastAppliedTheme.current = browserTheme
          }
        }
      } catch (error) {
        // Only log actual unexpected errors in development
        if (process.env.NODE_ENV === 'development') {
          console.warn('Auto-adapt theme failed (this is normal if no themes are configured):', error.message)
        }
      }
    }

    // Apply theme when browser theme changes
    applyAutoAdaptedTheme()
  }, [browserTheme, autoAdaptEnabled, isSupported])

  // Method to enable/disable auto-adaptation
  const setAutoAdaptation = (enabled) => {
    setAutoAdaptEnabled(enabled)
    if (!enabled) {
      lastAppliedTheme.current = null
    }
  }

  // Method to manually trigger theme adaptation
  const triggerAdaptation = async () => {
    if (!isSupported || typeof window === 'undefined') {
      console.warn('BrowserThemeDetector: Browser theme detection not supported')
      return false
    }

    try {

      if (window.SiteTheme) {
        const currentContext = window.location.pathname.startsWith('/admin') ? 'admin' : 'site'

        const response = await getCachedActiveTheme(currentContext, browserTheme)

        if (response.result) {
          await window.SiteTheme.setTheme(response.result, true)
          lastAppliedTheme.current = browserTheme
          return true
        }
      }
      return false
    } catch (error) {
      console.error('BrowserThemeDetector: Failed to trigger adaptation:', error)
      return false
    }
  }

  return {
    browserTheme,           // Current browser theme preference ('light' or 'dark')
    autoAdaptEnabled,       // Whether auto-adaptation is currently enabled
    isSupported,           // Whether browser supports theme detection
    setAutoAdaptation,     // Method to enable/disable auto-adaptation
    triggerAdaptation      // Method to manually trigger theme adaptation
  }
}

/**
 * BrowserThemeDetector Component
 *
 * React component that automatically detects and adapts to browser theme changes
 * Can be used as a provider or standalone component
 *
 * @param {Object} props Component properties
 * @param {boolean} props.autoAdapt - Enable automatic theme adaptation (default: true)
 * @param {Function} props.onThemeChange - Callback when browser theme changes
 * @param {React.ReactNode} props.children - Child components
 */
export const BrowserThemeDetector = ({
  autoAdapt = true,
  onThemeChange = null,
  children = null
}) => {
  const {
    browserTheme,
    autoAdaptEnabled,
    isSupported,
    setAutoAdaptation
  } = useBrowserThemeDetector()

  // Set auto-adaptation on mount
  useEffect(() => {
    setAutoAdaptation(autoAdapt)
  }, [autoAdapt, setAutoAdaptation])

  // Call onThemeChange callback when browser theme changes
  useEffect(() => {
    if (onThemeChange && typeof onThemeChange === 'function') {
      onThemeChange(browserTheme, { autoAdaptEnabled, isSupported })
    }
  }, [browserTheme, autoAdaptEnabled, isSupported, onThemeChange])

  // Render children or nothing
  return children || null
}

/**
 * Higher-order component that adds browser theme detection to any component
 *
 * @param {React.Component} WrappedComponent Component to wrap
 * @param {Object} options Configuration options
 * @param {boolean} options.autoAdapt Enable automatic adaptation (default: true)
 */
export const withBrowserThemeDetection = (WrappedComponent, options = {}) => {
  const { autoAdapt = true } = options

  return function BrowserThemeWrappedComponent(props) {
    const browserThemeProps = useBrowserThemeDetector()

    // Enable auto-adaptation on mount
    useEffect(() => {
      browserThemeProps.setAutoAdaptation(autoAdapt)
    }, [browserThemeProps, autoAdapt])

    return (
      <>
        <BrowserThemeDetector autoAdapt={autoAdapt} />
        <WrappedComponent {...props} browserTheme={browserThemeProps} />
      </>
    )
  }
}

/**
 * Manual theme adaptation trigger for use in components
 * Useful for buttons or manual triggers
 *
 * @returns {Object} Methods for manual theme adaptation
 */
export const useBrowserThemeAdapter = () => {
  const detectBrowserTheme = () => {
    if (typeof window === 'undefined') return 'light'

    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    return 'light'
  }

  const adaptToSystemTheme = async () => {
    try {
      const browserTheme = detectBrowserTheme()


      if (typeof window !== 'undefined' && window.SiteTheme) {
        const currentContext = window.location.pathname.startsWith('/admin') ? 'admin' : 'site'

        const response = await getCachedActiveTheme(currentContext, browserTheme)

        if (response.result) {
          await window.SiteTheme.setTheme(response.result, true)
          return { success: true, theme: response.result, browserTheme }
        }
      }

      return { success: false, error: 'SiteTheme API not available' }
    } catch (error) {
      console.error('Failed to adapt to system theme:', error)
      return { success: false, error: error.message }
    }
  }

  return {
    detectBrowserTheme,
    adaptToSystemTheme
  }
}

export { getCachedActiveTheme }
export default BrowserThemeDetector